import React from "react";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaGithub,
  FaHashtag,
  FaRocket,
  FaShieldAlt,
  FaLightbulb,
  FaArrowRight,
} from "react-icons/fa";

const Ibird: React.FC = () => {
  const features = [
    {
      icon: <FaUsers className="w-8 h-8" />,
      title: "Community-Driven",
      subtitle: "Your Voice Matters",
      description:
        "Governed democratically by its users, iBird evolves with the community at its heart. Your voice shapes its future.",
      technicalExplanation:
        "Unlike traditional social media controlled by corporations, iBird uses decentralized governance where community members vote on platform changes and improvements.",
      benefits: [
        "Democratic decision making",
        "Community ownership",
        "Transparent governance",
      ],
      link: "https://discord.gg/xM7SkkTEAG",
      linkText: "Join The Community",
    },
    {
      icon: <FaGithub className="w-8 h-8" />,
      title: "Open-Source",
      subtitle: "Complete Transparency",
      description:
        "With transparent, open-source code, iBird fosters trust and continuous innovation, free from hidden agendas.",
      technicalExplanation:
        "All iBird code is publicly available on GitHub, meaning anyone can inspect, verify, and contribute to the platform's development. No hidden algorithms or secret data collection.",
      benefits: [
        "Full code transparency",
        "Community contributions",
        "No hidden algorithms",
      ],
      link: "https://github.com/iassetsorg/Project-Harriet",
      linkText: "Start Building Freedom",
    },
    {
      icon: <FaHashtag className="w-8 h-8" />,
      title: "Hedera-Powered",
      subtitle: "Next-Gen Technology",
      description:
        "Harnessing Hedera's advanced technology, iBird offers a fast, secure, and equitable social media experience.",
      technicalExplanation:
        "Hedera Hashgraph provides lightning-fast transactions (3-5 seconds), ultra-low fees (under $0.01), and enterprise-grade security using advanced consensus algorithms.",
      benefits: [
        "Lightning fast posts",
        "Minimal transaction fees",
        "Bank-level security",
      ],
      link: "https://hedera.com/",
      linkText: "Learn about Hedera",
    },
  ];

  const techSpecs = [
    {
      icon: <FaRocket />,
      label: "Transaction Speed",
      value: "3-5 seconds",
      description: "Post and interact instantly",
    },
    {
      icon: <FaShieldAlt />,
      label: "Security Level",
      value: "Enterprise Grade",
      description: "Bank-level protection",
    },
    {
      icon: <FaLightbulb />,
      label: "Energy Efficiency",
      value: "Carbon Negative",
      description: "Environmentally friendly",
    },
  ];

  return (
    <div className="relative w-full py-16 md:py-32 overflow-hidden bg-gradient-to-b from-gray-900 to-gray-800">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 opacity-20 md:opacity-30"
        style={{
          backgroundImage: "url('/data.png')",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      />

      {/* Enhanced Floating Elements */}
      <motion.div
        animate={{ y: [0, -20, 0], rotate: [0, 180, 360] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-4 md:left-10 top-20 md:top-32 w-20 h-20 md:w-32 md:h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl"
      />
      <motion.div
        animate={{ y: [0, 20, 0], rotate: [360, 180, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-4 md:right-10 bottom-20 md:bottom-32 w-24 h-24 md:w-32 md:h-32 bg-gradient-to-r from-orange-400/20 to-pink-400/20 rounded-full blur-xl"
      />
      <motion.div
        animate={{ x: [0, 30, 0], y: [0, -15, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-1/4 top-1/2 w-16 h-16 md:w-24 md:h-24 bg-gradient-to-r from-green-400/15 to-blue-400/15 rounded-full blur-lg"
      />
      <motion.div
        animate={{ x: [0, -25, 0], y: [0, 20, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-1/3 top-1/4 w-12 h-12 md:w-20 md:h-20 bg-gradient-to-r from-purple-400/15 to-pink-400/15 rounded-full blur-lg"
      />

      <div className="relative container mx-auto px-4 md:px-6">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-20"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-6"
          >
            <h2 className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-4">
              Welcome to iBird
            </h2>
            <div className="flex items-center justify-center gap-2 mb-6">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center"
              >
                <FaHashtag className="text-white text-sm md:text-lg" />
              </motion.div>
              <span className="text-lg md:text-xl text-blue-300 font-semibold">
                The Future of Social Media
              </span>
            </div>
          </motion.div>

          <p className="text-lg md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8">
            iBird is a revolutionary social media platform built on Hedera by
            iAssets. It leverages Hedera technology for unparalleled speed,
            security, and innovation.
          </p>

          {/* What Makes iBird Different */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10 max-w-4xl mx-auto mb-12"
          >
            <h3 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center justify-center gap-2">
              <FaLightbulb className="text-yellow-400" />
              What Makes iBird Different?
            </h3>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Unlike traditional social media platforms controlled by big
              corporations, iBird is built by the community, for the community.
              It runs on Hedera's lightning-fast blockchain technology, ensuring
              your data stays secure while giving you true ownership of your
              content.
            </p>
          </motion.div>

          {/* Technical Specifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto"
          >
            {techSpecs.map((spec, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-white/10"
              >
                <div className="text-2xl md:text-3xl text-blue-400 mb-2 flex justify-center">
                  {spec.icon}
                </div>
                <h4 className="text-sm md:text-base font-semibold text-white mb-1">
                  {spec.label}
                </h4>
                <p className="text-lg md:text-xl font-bold text-blue-300 mb-1">
                  {spec.value}
                </p>
                <p className="text-xs md:text-sm text-gray-400">
                  {spec.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Main Features Grid */}
        <div className="grid gap-6 md:gap-8 lg:grid-cols-3 sm:grid-cols-1">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="h-full bg-white/10 backdrop-blur-sm rounded-3xl p-6 md:p-8 hover:bg-white/15 transition-all duration-300 border border-white/10 hover:border-white/20">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center mb-6">
                  <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl text-white mb-3 sm:mb-0 self-start">
                    {feature.icon}
                  </div>
                  <div className="sm:ml-4">
                    <h3 className="text-xl md:text-2xl font-bold text-white">
                      {feature.title}
                    </h3>
                    <p className="text-sm md:text-base text-blue-300 font-medium">
                      {feature.subtitle}
                    </p>
                  </div>
                </div>

                {/* Main Description */}
                <p className="text-gray-300 mb-4 text-base md:text-lg leading-relaxed">
                  {feature.description}
                </p>

                {/* Technical Explanation */}
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  whileInView={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.4, delay: index * 0.1 + 0.3 }}
                  className="bg-white/5 rounded-xl p-4 mb-6 border border-white/5"
                >
                  <h4 className="text-sm font-semibold text-blue-300 mb-2 flex items-center gap-2">
                    <FaLightbulb className="text-xs" />
                    How It Works
                  </h4>
                  <p className="text-sm text-gray-400 leading-relaxed mb-3">
                    {feature.technicalExplanation}
                  </p>

                  {/* Benefits List */}
                  <div className="space-y-1">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <motion.div
                        key={benefitIndex}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: 0.3,
                          delay: index * 0.1 + benefitIndex * 0.1 + 0.5,
                        }}
                        className="flex items-center gap-2 text-xs text-green-300"
                      >
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        {benefit}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Action Button */}
                <motion.a
                  href={feature.link}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center w-full px-6 py-3 text-white bg-gradient-to-r from-blue-500 to-purple-500 rounded-full hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 font-medium"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {feature.linkText}
                  <motion.span
                    className="ml-2"
                    animate={{ x: [0, 3, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <FaArrowRight className="text-sm" />
                  </motion.span>
                </motion.a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Ibird;
